import React, { useContext, useRef, useEffect, useState } from "react";
import { ccpUrl, connectRegion } from "./assets/config";
import TranslateText from "./translateText";
import Chatroom from "./Chatroom";

export const GlobalContext = React.createContext();

const languages = {
  en: "English",
  es: "Spanish",
  fr: "French",
  pt: "Portuguese",
  "zh-Hant": "Chinese Traditional (Mandarin)",
  ja: "Japanese",
  ko: "Korean",
  ro: "Romanian",
};

const translateProviders = {
  googleTranslate: 'Google Translate API',
  awsTranslate: 'Amazon Translate API'
}

function App() {
  const [currentContactId, setCurrentContactId] = useState(null);
  const [agentLanguage, setAgentLanguage] = useState(
    localStorage.getItem("agentLanguage") || "en",
  );
  const [connectedContacts, setConnectedContacts] = useState([]);
  const [messages, setMessages] = useState(
    JSON.parse(localStorage.getItem("chatMessages")) || {},
  );

  // Track the asynchronous loading status of both tools
  const [isCcpReady, setIsCcpReady] = useState(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);

  useEffect(() => {
    localStorage.setItem("chatMessages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (agentLanguage) {
      localStorage.setItem("agentLanguage", agentLanguage);
    }
  }, [agentLanguage]);

  // 1. Wait for the Core Streams API (window.connect) to inject via public/index.html
  useEffect(() => {
    const checkCcp = () => {
      if (window.connect && window.connect.core) {
        setIsCcpReady(true);
      } else {
        setTimeout(checkCcp, 200);
      }
    };
    checkCcp();
  }, []);

  // 2. Wait for and initialize the customer chat widget (window.amazon_connect)
  useEffect(() => {
    const initializeWidget = () => {
      if (window.amazon_connect) {
        window.amazon_connect('styles', { 
          iconType: 'CHAT', 
          openChat: { color: '#ffffff', backgroundColor: '#123456' }, 
          closeChat: { color: '#ffffff', backgroundColor: '#123456' } 
        });
        
        window.amazon_connect('snippetId', 'QVFJREFIaDhDQnJIRDNrbGlxb01PbmxZbjFOVzhZNWVpdEZ3VEoxV09TeFRGQ3ZDb2dGaGo5MVFIajNJdkYydVVmeHdzOE82QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNa3NxNCtxWkR3cHBPVGREU0FnRVFnQ3ZWblBOQ0VyUU4xamJvUHdkQXd0OEI4N3NOZU9NQ29GUFllTXFDUTZTbTBxN3EyeTA2ZzNWMnZ6WHg6OnRDd3hkYjYvYzNQNVZUeEF0VkNORTdHdWNzUXcwdTNpME1Kd3FhSEtRUVBTZDlPM085WlloMzhCMTNkd2FkRlJkUFFueElCVVQxdHZBbkRrVjl4TkRjTlhDd1JZUjVmYXVWY2NyOURUNkhMSXlMS3krMHZWYVN1dFRkeDNBeDFyQVRFdUZibG8zYjkyMVF6aGFDc2RpV01ocXRiazI2dz0=');
        
        window.amazon_connect('supportedMessagingContentTypes', [
          'text/plain', 
          'text/markdown', 
          'application/vnd.amazonaws.connect.message.interactive', 
          'application/vnd.amazonaws.connect.message.interactive.response'
        ]);

        setIsWidgetReady(true);
      }
    };

    const checkWidget = () => {
      if (window.amazon_connect) {
        initializeWidget();
      } else {
        setTimeout(checkWidget, 200);
      }
    };
    checkWidget();
  }, []);

  return (
    <GlobalContext.Provider
      value={{
        currentContactId,
        setCurrentContactId,
        agentLanguage,
        setAgentLanguage,
        connectedContacts,
        setConnectedContacts,
        messages,
        setMessages,
        languages,
        translateProviders
      }}
    >
      <div className="App">
        {/* Render application workspace ONLY when both platforms are authenticated */}
        {isCcpReady && isWidgetReady ? (
          <CCP />
        ) : (
          <div style={{ padding: "100px 20px", textAlign: "center", fontFamily: "sans-serif", color: "#333" }}>
            <h2>Initializing Contact Center Platforms...</h2>
            <p style={{ color: "#666" }}>Establishing links for Agent CCP Panel and Customer Chat Widget.</p>
            <div className="spinner" style={{ margin: "25px auto", width: "45px", height: "45px", border: "4px solid #f3f3f3", borderTop: "4px solid #123456", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>
    </GlobalContext.Provider>
  );
}

function CCP() {
  const [agentChatSessionState, setAgentChatSessionState] = useState([]);
  const [width, setWidth] = useState(window.innerWidth);
  const {
    setCurrentContactId,
    setConnectedContacts,
    setMessages,
  } = useContext(GlobalContext);

  function getEvents(contact, agentChatSession) {
    contact
      .getAgentConnection()
      .getMediaController()
      .then((controller) => {
        controller.onMessage((messageData) => {
          if (messageData.data.Type === "MESSAGE") {
            const isAgent =
              messageData.chatDetails.participantId ===
              messageData.data.ParticipantId;
            processChatText(
              messageData.data.Content,
              messageData.data.ContactId,
              isAgent ? "agent" : "customer",
              messageData,
            );
          }
        });
      });
  }

  async function processChatText(content, contactId, msgAuthor, messageData) {
    try {
      const currentAgentLanguage = localStorage.getItem("agentLanguage") || "en";
      const translator = new TranslateText(content, "auto", currentAgentLanguage);
      const translatedMessage = await translator.getTranslatedText();

      const newMsg = {
        contactId,
        msgAuthor,
        content: content,
        translatedText: translatedMessage.TranslatedText,
        sourceLanguage: translatedMessage.SourceLanguageCode || "auto",
        messageId: messageData.data.Id,
        timestamp: messageData.data.AbsoluteTime,
      };

      setMessages((prev) => ({
        ...prev,
        [contactId]: [...(prev[contactId] || []), newMsg],
      }));
    } catch (error) {
      console.error("Translation logic error:", error);
    }
  }

  function subscribeConnectEvents() {
    window.connect.core.onViewContact((event) => {
      setCurrentContactId(event.contactId);
    });

    if (window.connect.ChatSession) {
      window.connect.contact((contact) => {
        contact.onAccepted(async () => {
          const cnn = contact
            .getConnections()
            .find((cnn) => cnn.getType() === window.connect.ConnectionType.AGENT);
          const agentChatSession = await cnn.getMediaController();
          setCurrentContactId(contact.contactId);

          setAgentChatSessionState((prev) => [
            ...prev,
            { [contact.contactId]: agentChatSession },
          ]);
        });

        contact.onConnected(async () => {
          setCurrentContactId(contact.contactId);
          setConnectedContacts((prev) => [...prev, contact.contactId]);

          const cnn = contact
            .getConnections()
            .find((cnn) => cnn.getType() === window.connect.ConnectionType.AGENT);
          const agentChatSession = await cnn.getMediaController();

          setAgentChatSessionState((prev) => [
            ...prev,
            { [contact.contactId]: agentChatSession },
          ]);
          getEvents(contact, agentChatSession);
        });

        contact.onEnded(() => {
          setConnectedContacts((prev) => prev.filter((id) => id !== contact.contactId));
          setMessages((prev) => {
            const updatedMessages = { ...prev };
            delete updatedMessages[contact.contactId];
            return updatedMessages;
          });
        });

        contact.onDestroy(() => {
          setCurrentContactId(null);
        });
      });

      window.connect.agent((agent) => {
        agent.onStateChange((agentStateChange) => {
          console.log("Agent State Changed:", agentStateChange.newState);
        });
      });
    } else {
      setTimeout(subscribeConnectEvents, 3000);
    }
  }

  const ccpContainerRef = useRef();

  useEffect(() => {
    const updateWindowDimensions = () => setWidth(window.innerWidth);
    window.addEventListener("resize", updateWindowDimensions);
    return () => window.removeEventListener("resize", updateWindowDimensions);
  }, []);

  useEffect(() => {
    if (ccpContainerRef.current && window.connect && window.connect.core) {
      window.connect.core.initCCP(ccpContainerRef.current, {
        ccpUrl,
        loginPopup: true,
        loginPopupAutoClose: true,
        loginOptions: {
          autoClose: true,
          height: 578,
          width: 433,
          top: 0,
          left: 0,
        },
        region: connectRegion,
        softphone: {
          allowFramedSoftphone: true,
          disableRingtone: false,
        },
        pageOptions: {
          enableAudioDeviceSettings: true,
          enablePhoneTypeSettings: true,
        },
      });

      subscribeConnectEvents();
    }
  }, []);

  return (
    <div className="CCP" style={{ padding: "20px" }}>
      <div className={`d-flex ${width < 640 ? "flex-wrap" : ""}`}>
        <div>
          <div
            ref={ccpContainerRef}
            id="ccp-container"
            style={{
              width: width < 640 ? width : 380,
              height: width < 640 ? "calc(100vh / 2)" : 550,
              border: "1px solid #ddd",
              borderRadius: "4px"
            }}
          />
        </div>
        <div
          id="chatroom"
          style={{
            width: width < 640 ? width : 380,
            height: width < 640 ? "calc(100vh / 2)" : 600,
            marginLeft: width < 640 ? "0" : "20px",
            marginTop: width < 640 ? "20px" : "0"
          }}
        >
          <Chatroom session={agentChatSessionState} />
        </div>
      </div>
    </div>
  );
}

export default App;